import { Location } from '@angular/common'
import { Component, HostBinding, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { MatPaginator } from '@angular/material/paginator'

import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { GrowthToolkitClient } from 'src/app/shared/services/ppv.api'
import { TaskStat, TaskRow } from '../../models/dashboard.model';
import { MatDialog } from '@angular/material/dialog'
import { CreateNewTaskComponent } from './create-new-task/create-new-task.component'
import { LeaderService } from '../../services/leader.service'
import { ViewTaskModalComponent } from './view-task-modal/view-task-modal.component'
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component'
import { RecurrencePreviewDialogComponent } from './recurrence-preview-dialog.component'


@Component({
   selector: 'leader-toolkit-tasks',
   templateUrl: './tasks-page.component.html',
   styleUrls: ['./tasks-page.component.scss'],
   standalone: false,
})
export class LeaderToolkitTasksPage implements OnInit, OnDestroy, AfterViewInit {
   @HostBinding('class.full-flex-height') fullFlexHeight = true
   selectedHospitals: string[] = []
   hospitalSearch: string = ''
   selectedStatuses: string[] = []
   selectedGoal: string | null = null
   selectedAssignedType: string = ''
   priorityPracticeOnly: boolean = false
   globalFilter: string = ''
   assignedToMeFilter: boolean | null = null
   displayedColumns: string[] = ['assignedId','hospitalName', 'categoryName',  'task', 'assignedName', 'daysUntilDue', 'recurrence', 'statusName', 'createdBy', 'actions']
   dataSource!: MatTableDataSource<any>
   isLoading: boolean = true
   isAdmin: boolean = false
   taskList: any
   taskStats: TaskStat[] = []

   @ViewChild(MatSort) sort!: MatSort
   @ViewChild(MatPaginator) paginator!: MatPaginator
   quarterList: any
   selectedQuarter: string = '';
   duedate: any[] = []
   /** Quarter asked for by `?quarter=`, then narrowed by qtrList() to a real quarter name. */
   private requestedQuarter: string = ''

   @ViewChild(MatSort) set matSort(ms: MatSort) {
      this.sort = ms
      this.setDataSourceAttributes()
   }

   @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
      this.paginator = mp
      this.setDataSourceAttributes()
   }

   private setDataSourceAttributes(): void {
      if (this.dataSource && this.paginator && this.sort) {
         this.dataSource.paginator = this.paginator
         this.dataSource.sort = this.sort
      }
   }

   constructor(
      public router: Router,
      private route: ActivatedRoute,
      private dialog: MatDialog,
      private leaderService: LeaderService
   ) { }

   private pendingSearchHighlight: { id: number } | null = null
   private destroy$ = new Subject<void>()

   ngOnInit(): void {
      this.dataSource = new MatTableDataSource<any>([])
      // Quarter is read before the first fetch so the initial load is scoped correctly.
      // The task list itself is kicked off from qtrList() once the lookup resolves
      // 'default' to a concrete quarter name — loading here too would double-fetch.
      this.requestedQuarter = (this.route.snapshot.queryParamMap.get('quarter') ?? '').toString().trim();
      this.qtrList();
      this.checkAdminRole();

      // Pick up the search-highlight intent from NavigationExtras.state set
      // by the search results page. We use in-memory state (NOT query
      // params) so the destination URL stays clean — opening another row
      // from this page must not leave a stale ?highlight=... in the bar.
      // After consuming, we strip the marker from history.state so a hard
      // refresh doesn't accidentally re-open the popup.
      const state: any = (typeof history !== 'undefined' && history.state) || {}
      const sh = state.searchHighlight
      const id = Number(sh?.id)
      if (id && Number.isFinite(id) && id > 0) {
         this.pendingSearchHighlight = { id }
         try {
            const cleaned = { ...state }
            delete cleaned.searchHighlight
            history.replaceState(cleaned, '')
         } catch { /* non-browser env — ignore */ }
      }

      // Check for taskId query param in URL (deep link from email notification)
      this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
         const taskId = params['taskId']
         if (taskId && !this.pendingSearchHighlight) {
            const id = Number(taskId)
            if (Number.isFinite(id) && id > 0) {
               this.pendingSearchHighlight = { id }
               this.router.navigate([], { queryParams: { taskId: null }, queryParamsHandling: 'merge', replaceUrl: true })
            }
         }
      })

      this.leaderService.searchHighlightEvent
         .pipe(takeUntil(this.destroy$))
         .subscribe(({ id: highlightId }) => {
            this.openViewTask(highlightId);
         });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   checkAdminRole() {
      this.leaderService.hasAccessToManageUsersLtk().subscribe((res: any) => {
         this.isAdmin = res === true || res?.data === true;
      });
   }
   ngAfterViewInit(): void {
      this.dataSource.paginator = this.paginator
      this.dataSource.sort = this.sort
   }

   qtrList() {
      this.leaderService.getQtrList().subscribe((res: any) => {
         this.quarterList = res.data || []
         // A `?quarter=` in the URL wins; otherwise default to the current quarter.
         if (!this.requestedQuarter || this.requestedQuarter.toLowerCase() === 'default') {
            const currentQuarter = this.quarterList.find((q: any) => q?.currentQuarter)
            this.requestedQuarter = currentQuarter?.quarterName || ''
         }
         this.onQuarterChange()
      })
   }

   /** Quarter name to send to the API; '' means "let the server resolve the current quarter". */
   getResolvedQuarter(): string {
      const q = (this.selectedQuarter || '').toString().trim();
      return (q && q.toLowerCase() !== 'default') ? q : '';
   }

   onQuarterChange(quarter?: string): void {
      // `quarter` comes from the select; when omitted (initial load) fall back to the
      // value resolved by qtrList() from `?quarter=` or the current quarter.
      this.requestedQuarter = (quarter ?? this.requestedQuarter ?? '').toString().trim();
      this.selectedQuarter = this.requestedQuarter || 'default';
      // this.syncQuarterQueryParam(this.selectedQuarter);
      this.resetTableFilters();
      this.loadTaskList(this.getResolvedQuarter() || 'default');
   }

   /**
    * Quarter changes swap the whole row set, so client-side filters and paging from the
    * previous quarter must not leak into the new one. Unlike resetFilters() this does not
    * touch the quarter itself — that is a page scope, not a filter.
    */
   private resetTableFilters(): void {
      this.selectedHospitals = []
      this.selectedStatuses = []
      this.selectedGoal = null
      this.selectedAssignedType = ''
      this.globalFilter = ''
      this.assignedToMeFilter = null
      this.priorityPracticeOnly = false
      this.duedate = []
   }

   /** Keeps `?quarter=` in the URL in sync with the selection (bookmarkable / shareable). */
   private syncQuarterQueryParam(resolved: string): void {
      const inUrl = this.route.snapshot.queryParamMap.get('quarter') ?? '';
      const next = resolved && resolved !== 'default' ? resolved : '';
      if (inUrl === next) {
         return;
      }
      this.router.navigate([], {
         queryParams: { quarter: next || null },
         queryParamsHandling: 'merge',
         replaceUrl: true
      });
   }

   loadTaskList(quarter: any): void {
      this.isLoading = true
      this.leaderService.getTaskList(quarter).subscribe({
         next: (response: any) => {
            this.handleTaskListResponse(response)
            this.isLoading = false
         },
         error: (error) => {
            this.isLoading = false
         }
      })
   }

   handleTaskListResponse(response: any): void {
      if (response && response.data) {
         this.updateStats(response.data)
         this.taskList = response.data.tasksDetails || []
         this.taskData(this.taskList)
         this.applyPendingSearchHighlight()
      }
   }

   /**
    * If the user landed here from the search results page (state-flagged
    * via NavigationExtras.state.searchHighlight), scroll to the matching
    * row, briefly add .search-flash, and open the view dialog. Safe no-op
    * if the row isn't on screen — never throws.
    */
   private applyPendingSearchHighlight(): void {
      const pending = this.pendingSearchHighlight
      if (!pending) return
      // Consume immediately so this only runs once per navigation.
      this.pendingSearchHighlight = null

      setTimeout(() => {
         const el = document.getElementById('search-row-task-' + pending.id)
         if (el) {
            try {
               el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } catch { /* older browsers — ignore */ }
            el.classList.add('search-flash')
            setTimeout(() => el.classList.remove('search-flash'), 2500)
         }
         // openViewTask only needs the id — works even if the row isn't visible.
         this.openViewTask(pending.id)
      }, 0)
   }

   updateStats(data: any): void {
      this.taskStats = [
         { count: data.pastDueTasks || 0, label: 'Past Due Tasks', icon: 'assignment_late', colorClass: 'red' },
         { count: data.assignedToMe || 0, label: 'Assigned to Me', icon: 'how_to_reg', colorClass: 'blue' },
         { count: data.assignedToOthers || 0, label: 'Assigned to Others', icon: 'groups', colorClass: 'blue' },
         { count: data.completedTasks || 0, label: 'Completed Tasks', icon: 'assignment_turned_in', colorClass: 'green' },
      ]
   }

   taskData(taskList: any) {
      this.dataSource.data = taskList

      this.dataSource.sortingDataAccessor = (row: any, columnId: string): string | number => {
         // Assigned To sorting (case-insensitive)
         if (columnId === 'assignedName') {
            return (row.assignedTo || '').toString().toLowerCase();
         }
         // Due Date sorting (parse date)
         if (columnId === 'dueDate' || columnId === 'duedate2') {
            const date = new Date(row.dueDate);
            return isNaN(date.getTime()) ? 0 : date.getTime();
         }
         // Days Until Due sorting (numeric)
         if (columnId === 'daysUntilDue') {
            return this.getDaysUntilDue(row) ?? Number.MAX_SAFE_INTEGER;
         }
         // Category sorting (case-insensitive)
         if (columnId === 'categoryName') {
            return (row.categoryName || '').toString().toLowerCase();
         }
         // Status sorting (case-insensitive)
         if (columnId === 'statusName') {
            return (row.statusName || '').toString().toLowerCase();
         }
         // Default: case-insensitive for strings, value otherwise
         const value = row[columnId];
         if (value === null || value === undefined) {
            return '';
         }
         return typeof value === 'string' ? value.toLowerCase() : value;
      }

      this.dataSource.filterPredicate = (row: any, filter: string) => {
         let filterObj: any = {}
         try {
            filterObj = JSON.parse(filter)
         } catch (e) {
            filterObj = { search: filter }
         }

         const search = (filterObj.search || '').toString().toLowerCase()
         const hospitals = (filterObj.hospitalNames || []).map((h: string) => h.toString().toLowerCase().trim())
         const statuses = (filterObj.statuses || []).map((s: string) => s.toString().toLowerCase())
         const goal = (filterObj.goal || '').toString().toLowerCase()
         const assignedType = (filterObj.assignedType || '').toString().toLowerCase()
         const dueDates = filterObj.dueDates || []
         const priorityOnly = !!filterObj.priorityOnly

         const matchesSearch = !search || (
            (row.hospitalName || '').toString().toLowerCase().includes(search) ||
            (row.task || '').toString().toLowerCase().includes(search) ||
            (row.goalName || '').toString().toLowerCase().includes(search) ||
            (row.createdBy || '').toString().toLowerCase().includes(search)
         )

         const matchesHospital = hospitals.length === 0 || hospitals.includes((row.hospitalName || '').toString().toLowerCase().trim())
         const matchesStatus = statuses.length === 0 || statuses.includes((row.statusName || '').toString().toLowerCase())
         const matchesGoal = !goal || (row.goalName || '').toString().toLowerCase() === goal
         const matchesAssignedType = !assignedType || (row.assignedTypeName || '').toString().toLowerCase() === assignedType

         const assignedToMe = filterObj.assignedToMe
         const matchesAssignedTo = assignedToMe === null || assignedToMe === undefined ||
            (assignedToMe === true && row.assignedToId === 0) ||
            (assignedToMe === false && row.assignedToId !== 0)

         const matchesDueDate = dueDates.length === 0 || dueDates.some((d: string) => this.isSameDayString(row?.dueDate, d))
         const matchesPriority = !priorityOnly || row.isCritical === true

         return matchesSearch && matchesHospital && matchesStatus && matchesGoal && matchesAssignedType && matchesAssignedTo && matchesDueDate && matchesPriority
      }

      // Apply initial filter
      this.applyFilters()
   }

   get uniqueHospitals() {
      if (!this.taskList) return []
      const normalized = this.taskList
         .map((r: any) => (r.hospitalName ?? '').toString().trim())
         .filter((v: string) => v !== '')
      return normalized.filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)
   }

   get filteredHospitals() {
      const query = (this.hospitalSearch || '').toLowerCase().trim()
      if (!query) return this.uniqueHospitals
      return this.uniqueHospitals.filter((h: string) => h.toLowerCase().includes(query))
   }

   get uniqueStatuses() {
      if (!this.taskList) return []
      return this.taskList.map((r: any) => r.statusName).filter((v: any, i: any, a: any) => a.indexOf(v) === i)
   }

   get uniqueGoals() {
      if (!this.taskList) return []
      return this.taskList.map((r: any) => r.goalName).filter((v: any, i: any, a: any) => a.indexOf(v) === i)
   }

   get uniqueAssignedTypes() {
      if (!this.taskList) return []
      return this.taskList.map((r: any) => r.assignedTypeName).filter((v: any, i: any, a: any) => a.indexOf(v) === i)
   }

   togglePriorityPracticeFilter(): void {
      this.priorityPracticeOnly = !this.priorityPracticeOnly
      this.applyFilters()
   }

    get dueDateSortValue() {
       if (!this.taskList) return []
       return this.taskList.map((r: any) => r.dueDate).filter((v: any, i: any, a: any) => a.indexOf(v) === i)
    }

    get dueDateOptions() {
       return this.dueDateSortValue.map((d: any) => {
          const date = new Date(d)
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          const formatted = `${year}-${month}-${day}`
          return { label: formatted, value: formatted }
       }).sort((a, b) => b.value.localeCompare(a.value));
    }

   applyFilters() {
      const filterObj: any = {
         search: this.globalFilter || '',
         hospitalNames: this.selectedHospitals || [],
         statuses: this.selectedStatuses || [],
         goal: this.selectedGoal || '',
         assignedType: this.selectedAssignedType || '',
         dueDates: this.duedate || [],
         assignedToMe: this.assignedToMeFilter,
         priorityOnly: this.priorityPracticeOnly,
      }
      this.dataSource.filter = JSON.stringify(filterObj)
      if (this.dataSource.paginator) {
         this.dataSource.paginator.firstPage()
      }
   }

   setAssignedFilter(assignedToMe: boolean | null) {
      this.assignedToMeFilter = assignedToMe
      this.applyFilters()
   }

   resetFilters() {
      this.resetTableFilters()
      this.applyFilters()
      if (this.dataSource && this.dataSource.paginator) {
         this.dataSource.paginator.firstPage()
      }
      // Quarter is intentionally left alone — it scopes the whole page, so
      // "Reset Filters" must not silently jump back to the current quarter.
   }

   getStatusClass(status: string): string {
      switch (status) {
         case 'Past Due':
            return 'badge-red'
         case 'Complete':
         case 'Completed':
            return 'badge-green'
         case 'Pending':
            return 'badge-blue'
         default:
            return 'badge-blue'
      }
   }

   openViewTask(id: any) {
      const dialogRef = this.dialog.open(ViewTaskModalComponent, {
         data: {
            taskId: id,
            page: 'tasks',
            quarter: this.getResolvedQuarter()
         },
         width: '600px',
         disableClose: true,
      })
      dialogRef.afterClosed().subscribe(result => {
         if (result?.success) {
            this.loadTaskList(this.getResolvedQuarter() || 'default');
         }
      })
   }

   createNewtask() {
      const dialogRef = this.dialog.open(CreateNewTaskComponent, {
      })
      dialogRef.afterClosed().subscribe(result => {
         if (result == 'success') {
            this.loadTaskList(this.getResolvedQuarter() || 'default');
            this.resetFilters();
         }
      });
   }

   editTask(row: any) {
      const dialogRef = this.dialog.open(CreateNewTaskComponent, {
         data: {
            task: row
         }
      })
      dialogRef.afterClosed().subscribe(result => {
         if (result == 'success') {
            this.loadTaskList(this.selectedQuarter || 'default');
         }
      });
   }

   deleteTask(row: any) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
         width: '440px',
         data: {
            title: 'Delete Task',
            message: `Are you sure you want to delete the task "${row.task}"?`,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            showReasonInput: true,
            reasonLabel: 'Reason for deletion',
            reasonRequired: false
         }
      });

      dialogRef.afterClosed().subscribe(result => {
         const confirmed = result === true || result?.confirmed === true;
         if (confirmed) {
            const reason = result?.reason || '';
            this.isLoading = true;
            this.leaderService.deleteTask(row.taskId, reason).subscribe({
               next: (res: any) => {
                  this.isLoading = false;
                  this.leaderService.showNotification('Task deleted successfully!', false);
                  this.loadTaskList(this.selectedQuarter || 'default');
               },
               error: (err) => {
                  this.isLoading = false;
                  this.leaderService.showNotification('Failed to delete task', true);
               }
            });
         }
      });
   }

   addNewHospital() {
      this.router.navigate(['/leader-toolkit-hospital-editor', 'new'])
   }

    getDaysUntilDue(row: any): number | null {
      const dueDate = row.dueDate;
      if (!dueDate) return null;
      if (row.statusName.toLowerCase() === 'complete') return null;
      const parsedDate = new Date(dueDate)
      if (Number.isNaN(parsedDate.getTime())) return null

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const due = new Date(parsedDate)
      due.setHours(0, 0, 0, 0)

      const diffMs = due.getTime() - today.getTime()
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
   }

   isPastDue(dueDate: any): boolean {
      const days = this.getDaysUntilDue(dueDate)
      return days !== null && days < 0
   }

   private isSameDayString(dateValue: any, compareDateStr: string): boolean {
      if (!dateValue || !compareDateStr) return false
      const rowKey = this.toDateKey(dateValue)
      const filterKey = this.toDateKey(compareDateStr)
      if (!rowKey || !filterKey) return false
      return rowKey === filterKey
   }

   private toDateKey(value: any): string | null {
      if (typeof value === 'string') {
         const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
         if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`
         }
      }
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return null
      const year = parsed.getFullYear()
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      const day = String(parsed.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
   }

   openRecurrencePreview(row: any) {
     const occurrences = this.calculateOccurrences(row);
     this.dialog.open(RecurrencePreviewDialogComponent, {
       data: { occurrences },
       width: '500px'
     });
   }

   private calculateOccurrences(row: any): any[] {
     const startDate = row.dueDate ? new Date(row.dueDate) : new Date();
     const endDate = row.endDate ? new Date(row.endDate) : null;
     const interval = row.recurringInterval;
     const dueDays = row.dueDaysFromStartDate || 0;

     // Handle one-time tasks
     if (!interval || interval === 'One-time' || !endDate) {
       return [{
         occurrenceDate: startDate,
         calculatedDueDate: startDate
       }];
     }

     const occurrences = [];
     let currentStart = new Date(startDate);
     const end = new Date(endDate);

     let iterations = 0;
     while (currentStart <= end && iterations < 100) {
       iterations++;
       const occurrenceStart = new Date(currentStart);
       const occurrenceDue = new Date(currentStart);
       occurrenceDue.setDate(occurrenceDue.getDate() + dueDays);

       occurrences.push({
         occurrenceDate: occurrenceStart,
         calculatedDueDate: occurrenceDue
       });

       if (interval === 'Monthly') {
         currentStart.setMonth(currentStart.getMonth() + 1);
       } else if (interval === 'Quarterly') {
         currentStart.setMonth(currentStart.getMonth() + 3);
       } else if (interval === 'Annually') {
         currentStart.setFullYear(currentStart.getFullYear() + 1);
       } else {
         break;
       }
     }
     return occurrences;
   }

}
